import Decimal from 'decimal.js'

import type {
  CampaignBannerMerchandising,
  CategoryBrandingMerchandising,
  CompactCampaignBannerMerchandising,
  Recipe,
} from '@library/menu-service'
import type {
  CollectionMerchandisingItem,
  GradientInfoHeaderMerchandising,
  WaveLinkHeaderMerchandising,
} from '@library/menu-service/internal/types'

import { toCampaignBannerRecipeTagGroups } from './campaignBannerRecipeTagGroups'
import type {
  CampaignBannerCtaItem,
  CampaignBannerHeaderContentBlock,
  CampaignBannerRecipeItem,
  CategoryBrandingContentBlock,
  CompactCampaignBannerRecipeItem,
  CompactCampaignBannerHeaderContentBlock,
  GradientInfoHeaderContentBlock,
  WaveLinkHeaderContentBlock,
} from './contentBlockTypes'

type GetRecipeById = (id: string) => Recipe | null

const toDisplayType = (recipe: Recipe): 'standard' | 'ready-to-heat' =>
  recipe.preparationType === 'heat-only' ? 'ready-to-heat' : 'standard'

// Surcharge logic below taken from experience-api
// see apps/experience-api/src/modules/support/pricing/pricing.generator.ts
const roundToFixed = (num: number | string | Decimal, decimals: number): string => {
  if (!Number.isInteger(decimals) || decimals < 0) {
    throw new TypeError('decimals must be a non-negative integer')
  }

  return new Decimal(num).toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP).toFixed(decimals)
}

const toSurchargeLabel = (pricePerPortion: number) => {
  const roundedPrice = Math.sign(pricePerPortion) * Math.round(Math.abs(pricePerPortion))

  if (roundedPrice === 0) {
    return ''
  }

  if (roundedPrice >= -99 && roundedPrice <= 99) {
    if (roundedPrice < 0) {
      return `Save ${Math.abs(roundedPrice)}p`
    }

    return `+${roundedPrice}p`
  }

  if (roundedPrice < 0) {
    return `Save £${roundToFixed(Math.abs(roundedPrice) / 100, 2)}`
  }

  return `+£${roundToFixed(roundedPrice / 100, 2)}`
}

const toCampaignBannerRecipeItem = (recipe: Recipe) => ({
  type: 'recipe' as const,
  id: recipe.id,
  image: { crops: recipe.images[0]?.crops ?? [] },
  title: recipe.name,
  tagGroups: toCampaignBannerRecipeTagGroups(recipe),
  displayType: toDisplayType(recipe),
  isAvailable: recipe.isAvailable,
  ...(recipe.surcharge
    ? {
        pricing: {
          perPortionSurcharge: toSurchargeLabel(recipe.surcharge.pricePerPortion),
          isNegative: recipe.surcharge.pricePerPortion < 0,
        },
      }
    : {}),
  ...(recipe.shelfLife ? { shelfLife: recipe.shelfLife } : {}),
})

const toCompactCampaignBannerRecipeItem = (recipe: Recipe) => ({
  type: 'recipe' as const,
  id: recipe.id,
  image: { crops: recipe.images[0]?.crops ?? [] },
  title: recipe.name,
  displayType: toDisplayType(recipe),
  isAvailable: recipe.isAvailable,
})

const toCampaignBannerCtaItem = (
  item: Extract<CollectionMerchandisingItem, { type: 'cta' }>,
): CampaignBannerCtaItem => ({
  type: item.type,
  title: item.title,
  ctaText: item.ctaText,
  targetCategoryId: item.targetCategoryId,
})

export const toCategoryBrandingContentBlock = (
  merchandising: CategoryBrandingMerchandising,
): CategoryBrandingContentBlock => ({
  type: 'category-branding',
  backgroundImage: {
    type: 'background-image' as const,
    crops: merchandising.backgroundImage.crops,
  },
  backgroundColor: merchandising.backgroundColor,
  textColor: merchandising.textColor,
  title: merchandising.title,
  description: merchandising.description,
})

export const toWaveLinkHeaderContentBlock = (
  merchandising: WaveLinkHeaderMerchandising,
): WaveLinkHeaderContentBlock => ({
  type: 'wave-link-header',
  title: merchandising.title,
  description: merchandising.description,
  headerImage: merchandising.headerImage,
  categorySlug: merchandising.collectionSlug,
  categoryId: merchandising.collectionId,
  color: merchandising.color,
  backgroundColor: merchandising.backgroundColor,
})

export const toGradientInfoHeaderContentBlock = (
  merchandising: GradientInfoHeaderMerchandising,
): GradientInfoHeaderContentBlock => ({
  type: 'gradient-info-header',
  title: merchandising.title,
  description: merchandising.description,
  headerImage: merchandising.headerImage,
  color: merchandising.color,
  gradientColor: merchandising.gradientColor,
  image: merchandising.image,
  imageLocation: merchandising.imageLocation,
})

export const toCampaignBannerContentBlock = (
  merchandising: CampaignBannerMerchandising,
  getRecipeById: GetRecipeById,
): CampaignBannerHeaderContentBlock => {
  const items = merchandising.items.reduce<(CampaignBannerRecipeItem | CampaignBannerCtaItem)[]>(
    (acc, item) => {
      if (item.type === 'cta') {
        acc.push(toCampaignBannerCtaItem(item))

        return acc
      }

      const recipe = getRecipeById(item.id)
      if (recipe) {
        acc.push(toCampaignBannerRecipeItem(recipe))
      }

      return acc
    },
    [],
  )

  return {
    type: 'campaign-banner',
    backgroundImage: {
      type: 'background-image' as const,
      crops: merchandising.backgroundImage.crops,
    },
    backgroundColor: merchandising.backgroundColor,
    textColor: merchandising.textColor,
    subtitle: merchandising.subtitle,
    title: merchandising.title,
    description: merchandising.description,
    items,
  }
}

export const toCompactCampaignBannerContentBlock = (
  merchandising: CompactCampaignBannerMerchandising,
  getRecipeById: GetRecipeById,
): CompactCampaignBannerHeaderContentBlock => {
  const items = merchandising.items.reduce<CompactCampaignBannerRecipeItem[]>((acc, item) => {
    const recipe = getRecipeById(item.id)

    if (recipe) {
      acc.push(toCompactCampaignBannerRecipeItem(recipe))
    }

    return acc
  }, [])

  return {
    type: 'compact-campaign-banner',
    backgroundImage: {
      type: 'background-image' as const,
      crops: merchandising.backgroundImage.crops,
    },
    backgroundColor: merchandising.backgroundColor,
    textColor: merchandising.textColor,
    title: merchandising.title,
    description: merchandising.description,
    ctaText: merchandising.ctaText,
    targetCategoryId: merchandising.targetCategoryId,
    items,
  }
}
